import { isBrowser } from '@/helpers/BrowserHelper';
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import queryString from 'query-string';
import { useLocation } from '@reach/router';
import axios from 'axios';
import { SocialJoinControllerService } from '@/__generated__/CommonApi';

export interface KakaoUser {
  accessToken: string;
  id?: string;
  name?: string;
  email?: string;
  birthYear?: string;
  birthday?: string;
  phoneNumber?: string;
  // 이하 가공 데이터
  convertBirthYmd?: string;
  convertPhoneNumber?: string;
}

export interface KakaoSimpleJoinResult {
  resultCode: string; // 성공여부 (success, failed, existWebId)
  resultState: string; //
  resultMessage: string; // 결과 메시지
  // KS-2783: 기존 웹ID 안내 팝업 데이터
  existWebId?: string;
  maskedName?: string;
  donorId?: string;
  socialType?: string;
}

interface IProps {
  clientId: string;
  callbackUrl: string;
  isReturnProfile: boolean;
  isPopup?: boolean;
  role: 'login' | 'join' | 'auth' | 'unlink' | 'logout'; //string; //login, join, auth, unlink, logout
  render: (props: any) => ReactElement;
  onSuccess: (result: KakaoUser) => void;
  onFailure?: (msg: string) => void;
}

const KAKAO_SDK_SCRIPT_ID = process.env.KAKAO_SDK_ID || '';
const SOCIAL_TYPE = 'K';

const loadSdk = () => {
  const script = document.createElement(`script`);
  script.id = KAKAO_SDK_SCRIPT_ID;
  script.src = process.env.KAKAO_SDK_URL || '';
  document.head.appendChild(script);
  return script;
};
// 카카오 연결 끊기
export const KakaoUnlink = async (
  kakaoInfo: KakaoUser,
  isUnlinkMemberInfo: boolean,
) => {
  const result = {
    resultCode: 'failed',
    resultState: '',
    resultMessage: '',
  } as KakaoSimpleJoinResult;

  // SNS 계정 연결 끊기
  if (isUnlinkMemberInfo === true) {
    const unlinkSocial = await SocialJoinControllerService.unlinkSocialFromMemberUsingPost(
      {
        socialType: SOCIAL_TYPE,
        socialId: kakaoInfo.id || '',
      },
    );
    result.resultCode = unlinkSocial.resultCode || 'failed';

    if (unlinkSocial.resultCode !== 'success') {
      result.resultMessage = `카카오 연결 끊기 : ${
        unlinkSocial?.data?.returnMessage || '카카오 연결 끊기에 실패했습니다.'
      }`;
      return result;
    }
  }
  // 카카오 연결 끊기
  await window.Kakao.API.request({
    url: '/v1/user/unlink',
  })
    .then((response: any) => {
      //console.log('Kakao unlink >> response : ', response);
      result.resultCode = 'success';
      result.resultMessage = '카카오 연결 끊기 : 연결 끊기가 완료되었습니다.';
    })
    .catch((error: any) => {
      //console.log('Kakao unlink >> error : ', error);
      result.resultCode = 'failed';
      result.resultMessage =
        '카카오 연결 끊기 : 연결 끊기에 실패했습니다. 카카오 계정에서 연결 끊기를 하세요.';
    });
  return result;
};

// 카카오 간편 가입
export const KakaoSimpleJoin = async (kakaoInfo: KakaoUser) => {
  const result = {
    resultCode: 'failed',
    resultState: '',
    resultMessage: '',
  } as KakaoSimpleJoinResult;
  try {
    // 카카오인증 액세스토큰 확인
    if (!kakaoInfo.accessToken) {
      result.resultMessage = '카카오 간편가입 : 카카오 인증에 실패했습니다.';
      return result;
    }
    // 소셜가입시 이메일 중복체크
    const existsEmailInfo = await SocialJoinControllerService.socialJoinBeforeEmailDuplicateCheckUsingPost(
      {
        email: kakaoInfo.email || '',
        id: kakaoInfo.id || '',
        snsDiv: SOCIAL_TYPE,
      },
    );
    if (existsEmailInfo.resultCode !== 'success') {
      result.resultMessage = '카카오 간편가입 : 이미 가입된 이메일 입니다.';
      return result;
    }
    if (existsEmailInfo?.data?.existUserType === 'G') {
      // 기존 계정 연결
      /* 250904 : 기존 계정 연결 기능 제거
      const joinResult = await SocialJoinControllerService.linkSocialToMemberUsingPost(
        {
          accessToken: kakaoInfo.accessToken,
          socialType: SOCIAL_TYPE,
        },
      );
      result.resultCode = joinResult.resultCode || 'failed';
      if (joinResult.resultCode === 'success') {
        result.resultMessage = '기존 계정에 연결되었습니다.';
        result.resultState = 'LINKED';
      } else {
        result.resultMessage = `카카오 간편가입 : ${joinResult?.data?.returnMessage}`;
      }
      */
      result.resultMessage = `카카오 간편가입 : ${existsEmailInfo?.data?.returnMessage}`;
    } else {
      // 신규 가입 처리
      const joinResult = await SocialJoinControllerService.socialSimpleJoinUsingPost(
        {
          accessToken: kakaoInfo.accessToken,
          socialType: SOCIAL_TYPE,
        },
      );
      result.resultCode = joinResult.resultCode || 'failed';
      if (result.resultCode === 'success') {
        result.resultMessage = '회원가입이 완료되었습니다.';
        result.resultState = 'JOIN';
      } else if (result.resultCode === 'existWebId') {
        // KS-2783: 기존 웹ID 발견
        const data = joinResult.data as any;
        result.existWebId = data?.existWebId;
        result.maskedName = data?.maskedName;
        result.donorId = data?.donorId;
        result.socialType = data?.socialType || SOCIAL_TYPE;
      } else {
        result.resultMessage = `카카오 간편가입 : ${joinResult?.data?.returnMessage}`;
      }
    }
  } catch (e) {
    result.resultMessage = '카카오 간편가입 : 간편가입에 실패했습니다. (0)';
    console.log(e);
  }
  return result;
};

/**
 * 카카오 로그인
 * @param props
 */
const KakaoLogin: FC<IProps> = ({
  clientId,
  render,
  onSuccess,
  onFailure,
  isReturnProfile,
  isPopup,
  role,
  ...props
}) => {
  const location = useLocation();
  const { code, state } = queryString.parse(location.search);
  const [isLoadScript, setIsLoadScript] = useState(false);
  const [loadAccessCode, setLoadAccessCode] = useState(false);
  const [requestTokenCnt, setRequestTokenCnt] = useState(0);
  // 팝업에 필요한 정보
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const popupRef = useRef(null);
  const intervalRef = useRef(null);

  const initLoginButton = (props: Omit<IProps, 'render'>) => {
    if (typeof window === 'undefined') return;
    if (!isBrowser) {
      return;
    }
    const { Kakao } = window;
    if (!Kakao) {
      return;
    }
    setIsLoadScript(true);
  };

  const loadScript = (props: Omit<IProps, 'render'>) => {
    if (document.querySelectorAll(`#${KAKAO_SDK_SCRIPT_ID}`).length === 0) {
      const script = loadSdk();
      script.onload = () => initLoginButton(props);
    } else {
      // script가 이미 로드된 경우, init만 실행
      initLoginButton(props);
    }
  };

  // 카카오 로그인 팝업 열기
  const openKakaoLogin = () => {
    if (!isMountedRef.current) return;

    setIsOpenPopup(true);
    setError(null);

    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      `${props.callbackUrl}`,
    )}&response_type=code&state=kko&prompt=select_account&throughTalk=true&scope=name,account_email,phone_number,birthday,birthyear`;

    const popup = window.open(
      kakaoAuthUrl,
      'kakaoLogin',
      'width=500,height=600,scrollbars=yes,resizable=yes',
    );

    popupRef.current = popup;

    // 팝업이 닫혔는지 주기적으로 체크
    intervalRef.current = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;

        // 컴포넌트가 마운트되어 있을 때만 상태 업데이트
        if (isMountedRef.current) {
          setIsOpenPopup(false);
        }
      }
    }, 1000);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isBrowser) {
      return;
    }
    const { Kakao } = window;

    if (!Kakao) {
      loadScript({
        clientId,
        isReturnProfile,
        role,
        onSuccess,
        onFailure,
        ...props,
      });
    }
    // 인증이후인지 확인
    if (state === 'kko' && code && Kakao) {
      if (!Kakao.isInitialized()) {
        Kakao.init(clientId);
      }
      if (loadAccessCode) {
        return;
      }
      setLoadAccessCode(true);
      setAccessCode();
    }
  }, [code, state, isLoadScript, loadAccessCode]);

  const setOnSeccessData = (accessToken: string, isReturnProfile: boolean) => {
    if (isPopup === true) {
      if (!isMountedRef.current) return;
    }

    if (!isReturnProfile) {
      if (isPopup === true) {
        // 부모 창에 성공 메시지 전송
        window.opener?.postMessage(
          {
            type: 'KAKAO_AUTH_SUCCESS',
            callbackData: {
              accessToken: `${accessToken}`,
            },
          },
          window.location.origin,
        );
        window.close();
      } else {
        onSuccess({
          accessToken: `${accessToken}`,
        });
      }
    } else {
      if (requestTokenCnt > 0) return;

      setRequestTokenCnt(requestTokenCnt + 1);

      window.Kakao.API.request({
        url: '/v2/user/me',
        data: {
          property_keys: [
            'kakao_account.name',
            'kakao_account.email',
            'kakao_account.birthyear',
            'kakao_account.birthday',
            'kakao_account.phone_number',
          ],
        },
      })
        .then((response: any) => {
          const birthYmd =
            response?.kakao_account?.birthyear &&
            response?.kakao_account?.birthday
              ? `${response?.kakao_account?.birthyear}${response?.kakao_account?.birthday}`
              : '';
          const getPhoneNumber = () => {
            const phoneNumber = response?.kakao_account?.phone_number;
            // 데이터 없음
            if (!phoneNumber) return '';

            const phoneNumberDiv = phoneNumber.split(' ');
            if (phoneNumberDiv.length === 1) {
              // 국가번호 없음
              return phoneNumberDiv[0].replaceAll('-', '');
            } else {
              // 국가번호 있음
              return `0${phoneNumberDiv[1].replaceAll('-', '')}`;
            }
          };

          if (isPopup === true) {
            // 부모 창에 성공 메시지 전송
            window.opener?.postMessage(
              {
                type: 'KAKAO_AUTH_SUCCESS',
                callbackData: {
                  accessToken: window.Kakao.Auth.getAccessToken(),
                  id: response?.id,
                  name: response?.kakao_account?.name,
                  email: response?.kakao_account?.email,
                  birthYear: response?.kakao_account?.birthyear,
                  birthday: response?.kakao_account?.birthday,
                  phoneNumber: response?.kakao_account?.phone_number,
                  convertBirthYmd: birthYmd,
                  convertPhoneNumber: getPhoneNumber(),
                },
              },
              window.location.origin,
            );
            window.close();
          } else {
            onSuccess({
              accessToken: window.Kakao.Auth.getAccessToken(),
              id: response?.id,
              name: response?.kakao_account?.name,
              email: response?.kakao_account?.email,
              birthYear: response?.kakao_account?.birthyear,
              birthday: response?.kakao_account?.birthday,
              phoneNumber: response?.kakao_account?.phone_number,
              convertBirthYmd: birthYmd,
              convertPhoneNumber: getPhoneNumber(),
            });
          }
        })
        .catch((error: any) => {
          if (onFailure) {
            onFailure(error.msg);
          } else {
            console.error(error);
          }
        });
    }
    return '';
  };

  const setAccessCode = useCallback(async () => {
    if (!window.Kakao.Auth.getAccessToken()) {
      const form = new FormData();
      form.append('grant_type', 'authorization_code');
      form.append('client_id', clientId);
      form.append('redirect_uri', props.callbackUrl);
      form.append('code', `${code}`);
      await axios
        .post(`https://kauth.kakao.com/oauth/token`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: false,
        })
        .then((res) => {
          if (res.data.access_token) {
            window.Kakao.Auth.setAccessToken(`${res.data.access_token}`);
            setOnSeccessData(`${res.data.access_token}`, isReturnProfile);
          } else {
            if (onFailure) {
              onFailure('액세스코드가 없습니다.');
            } else {
              console.error('액세스코드가 없습니다.');
            }
          }
        })
        .catch((e) => {
          if (onFailure) {
            onFailure(e);
          } else {
            console.error(e);
          }
        });
    } else {
      setOnSeccessData(window.Kakao.Auth.getAccessToken(), isReturnProfile);
    }
  }, []);

  // 메시지 이벤트 리스너 등록 (팝업에서 인증 결과 받기)
  useEffect(() => {
    // 컴포넌트 마운트 상태 설정
    isMountedRef.current = true;

    const handleMessage = (event) => {
      // 보안을 위해 origin 체크
      if (event.origin !== window.location.origin) return;
      // 컴포넌트가 언마운트되었으면 처리하지 않음
      if (!isMountedRef.current) return;
      if (event.data.type === 'KAKAO_AUTH_SUCCESS') {
        //handleAuthSuccess(event.data.code);
        onSuccess(event.data.callbackData);
      } else if (event.data.type === 'KAKAO_AUTH_ERROR') {
        if (isMountedRef.current) {
          setError(event.data.error);
          setIsOpenPopup(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // 클린업 함수
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('message', handleMessage);

      // 팝업이 열려있다면 닫기
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }

      // interval 정리
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return render({
    onClick: () => {
      if (window.Kakao) {
        if (isPopup === true) {
          openKakaoLogin();
          return;
        }
        window.Kakao.cleanup();
        window.Kakao.init(clientId);
        window.Kakao.Auth.setAccessToken(null);
        window.Kakao.Auth.authorize({
          redirectUri: `${props.callbackUrl}`,
          // prompt: role === 'unlink' ? 'none' : 'select_account',
          prompt: 'select_account', // login (매번 새로 로그인) | select_account (간편로그인) | none (자동로그인-인앱)
          scope: 'name,account_email,phone_number,birthday,birthyear',
          state: 'kko',
          throughTalk: true,
        });
      }
    },
  });
};

export default KakaoLogin;
